/**
 * MAVLink 会话层。
 *
 * 职责是「这帧是谁的」与「链路现在算不算通」，具体是：
 *   - 路由：按 `sysid` / `compid` 归集，弄清链路上到底有几台机体、几个组件；
 *   - 心跳与在线判定：超过阈值没有新帧即判定离线（**绝不**让界面显示"一片正常"而实际已断）；
 *   - 发送：维护发送序号，按通道配置组帧、可选签名；
 *   - 速率协商：优先 `MAV_CMD_SET_MESSAGE_INTERVAL`，失败退回 legacy `REQUEST_DATA_STREAM`；
 *   - 统计：帧率、字节率、CRC 错误、未知消息 —— 这些数字直接进链路诊断面板。
 *
 * 它**不**知道 ArduPilot 的飞行模式、参数、任务长什么样，那是 `@hgc/core` 的事。
 */
import {
  MavlinkParser,
  MavlinkSignatureVerifier,
  MavlinkSigner,
  encodeFrame,
  messageByName,
  type MavlinkFrame,
  type MessageDefinition,
  type MessageValues,
  type UnknownMessageInfo,
} from '@hgc/mavlink';

import { RateMeter } from './rate';
import type { Transport, TransportState } from './transport';

/** 默认的在线判定阈值：3 秒没有新帧即判定断链 */
export const DEFAULT_HEARTBEAT_TIMEOUT_MS = 3000;

/** 地面站默认的系统 ID（与常见的飞控默认值 1 错开） */
export const DEFAULT_GROUND_STATION_SYSTEM_ID = 255;
export const DEFAULT_GROUND_STATION_COMPONENT_ID = 190;

export type LinkState =
  /** 还没开始连接 */
  | 'idle'
  | 'connecting'
  /** 有链路、也有机体在报心跳 */
  | 'online'
  /** 链路打开了但一直没有收到任何帧 */
  | 'silent'
  /** 曾经有过机体，现在全部超时 */
  | 'offline'
  | 'error';

export interface VehiclePresence {
  /** `${systemId}:${componentId}`，用作稳定键 */
  readonly key: string;
  readonly systemId: number;
  readonly componentId: number;
  readonly firstSeenAt: number;
  lastSeenAt: number;
  /** 累计收到的帧数 */
  frames: number;
  /** 是否在心跳阈值内 */
  online: boolean;
}

export interface LinkStats {
  readonly framesIn: number;
  readonly framesOut: number;
  readonly bytesIn: number;
  readonly bytesOut: number;
  readonly framesPerSecond: number;
  readonly bytesPerSecond: number;
  readonly crcErrors: number;
  readonly unknownMessages: number;
  readonly droppedBytes: number;
  readonly malformedFrames: number;
  readonly truncatedPayloads: number;
  readonly signatureFailures: number;
  /** 最近一次收到帧的时刻；从未收到为 null */
  readonly lastFrameAt: number | null;
  /** 从最近一次收到帧到现在过了多久；从未收到为 null */
  readonly silenceMs: number | null;
}

export interface SessionOptions {
  readonly transport: Transport;
  /** 本机（地面站）的系统 ID */
  readonly systemId?: number;
  readonly componentId?: number;
  /** 默认 MAVLink 2。只有对端确实不支持 MAVLink 2 时才设为 1。 */
  readonly version?: 1 | 2;
  readonly signer?: MavlinkSigner;
  readonly verifier?: MavlinkSignatureVerifier;
  readonly now?: () => number;
  readonly heartbeatTimeoutMs?: number;
  readonly onFrame?: (frame: MavlinkFrame) => void;
  readonly onPresenceChange?: (vehicles: readonly VehiclePresence[]) => void;
  readonly onStateChange?: (state: LinkState, detail?: string) => void;
  readonly onUnknownMessage?: (info: UnknownMessageInfo) => void;
  readonly onSignatureFailure?: (info: { frame: MavlinkFrame; reason: string }) => void;
}

export class MavlinkSession {
  private readonly transport: Transport;
  private readonly parser: MavlinkParser;
  private readonly now: () => number;
  private readonly heartbeatTimeoutMs: number;
  private readonly version: 1 | 2;
  private readonly signer: MavlinkSigner | undefined;
  private readonly systemId: number;
  private readonly componentId: number;
  private readonly options: SessionOptions;

  private readonly presence = new Map<string, VehiclePresence>();
  private sequence = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private unsubscribe: (() => void) | null = null;

  private currentState: LinkState = 'idle';
  private stateDetail: string | undefined;

  private framesIn = 0;
  private framesOut = 0;
  private bytesIn = 0;
  private bytesOut = 0;
  private lastFrameAt: number | null = null;
  private readonly inRate: RateMeter;
  private readonly outRate: RateMeter;

  constructor(options: SessionOptions) {
    this.options = options;
    this.transport = options.transport;
    this.now = options.now ?? (() => Date.now());
    this.heartbeatTimeoutMs = options.heartbeatTimeoutMs ?? DEFAULT_HEARTBEAT_TIMEOUT_MS;
    this.version = options.version ?? 2;
    this.signer = options.signer;
    this.systemId = options.systemId ?? DEFAULT_GROUND_STATION_SYSTEM_ID;
    this.componentId = options.componentId ?? DEFAULT_GROUND_STATION_COMPONENT_ID;
    this.inRate = new RateMeter(this.now());
    this.outRate = new RateMeter(this.now());

    this.parser = new MavlinkParser({
      ...(options.verifier === undefined ? {} : { verifier: options.verifier }),
      now: this.now,
      onFrame: (frame) => this.handleFrame(frame),
      ...(options.onUnknownMessage === undefined
        ? {}
        : { onUnknownMessage: options.onUnknownMessage }),
      ...(options.onSignatureFailure === undefined
        ? {}
        : {
            onSignatureFailure: ({ frame, rejection }) =>
              options.onSignatureFailure?.({ frame, reason: rejection.reason }),
          }),
    });
  }

  get transportKind(): Transport['kind'] {
    return this.transport.kind;
  }

  get transportLabel(): string {
    return this.transport.label;
  }

  get state(): LinkState {
    return this.currentState;
  }

  get stateReason(): string | undefined {
    return this.stateDetail;
  }

  /** 本机标识，界面上用于区分"我发的"与"飞控发的" */
  get localIdentity(): { systemId: number; componentId: number } {
    return { systemId: this.systemId, componentId: this.componentId };
  }

  async open(): Promise<void> {
    if (this.unsubscribe !== null) return;

    this.unsubscribe = this.transport.subscribe((event) => {
      if (event.type === 'data') {
        this.bytesIn += event.bytes.length;
        this.parser.push(event.bytes);
        return;
      }
      this.handleTransportState(event.state, event.detail);
    });

    /*
     * 清空上一条链路可能留下的残余，**必须发生在打开新链路之前**。
     *
     * 这里踩过一个真实的坑：这段清理原本写在 `await transport.open()` **之后**，
     * 而有些传输是**在 open() 期间就同步交付数据**的（内置模拟机就是如此）。
     * 于是"清理陈旧状态"实际变成了"丢掉刚收到的数据"：刚发现的机体会被立刻抹掉、
     * 第一帧的时间戳被清零，界面在连上之后约 100ms 里显示"已连接 · 等待心跳"，
     * 直到下一次心跳到达才恢复。
     *
     * 但清理本身不能省：旧链路的半截帧与新链路的数据拼在一起会产生随机"帧"。
     * 只是它的正确位置是「新链路开始之前」，而不是「新链路开始之后」。
     */
    this.parser.reset();
    this.presence.clear();
    this.lastFrameAt = null;
    this.inRate.reset(this.now());
    this.outRate.reset(this.now());

    this.setState('connecting');
    try {
      await this.transport.open();
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      this.setState('error', `打开链路失败：${detail}`);
      throw error;
    }

    /*
     * 只有在"打开后什么都没收到"时才宣告静默。
     *
     * 原来的写法是 `if (currentState !== 'silent') setState('silent')`，
     * 它会**覆盖掉已经算出来的结论**：若传输在 open() 期间就交付了心跳，
     * 状态已经是 online，这一行会把它压回 silent；若 handleTransportState
     * 刚因为底层失败置成 error，这一行也会把真实错误盖成"静默"。
     * 判据应该是"我们还停在 connecting 吗"，而不是"当前不是 silent 吗"。
     */
    if (this.currentState === 'connecting') this.setState('silent');

    // 定时检查在线状态。频率是阈值的三分之一：既能及时判定，也不至于空转。
    const interval = Math.max(50, Math.floor(this.heartbeatTimeoutMs / 3));
    this.timer = setInterval(() => this.tick(), interval);
  }

  async close(): Promise<void> {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.unsubscribe?.();
    this.unsubscribe = null;
    await this.transport.close();
    this.presence.clear();
    this.setState('idle');
  }

  /**
   * 组装并发送一条消息。
   *
   * 消息可以用名字或定义对象指定 —— 用名字更可读（`'COMMAND_LONG'`），
   * 而且写错名字会当场报错，不会静默地发到另一条消息上。
   */
  async send(message: MessageDefinition | string, values: MessageValues): Promise<void> {
    const definition = typeof message === 'string' ? messageByName(message) : message;
    if (definition === undefined) {
      throw new Error(`协议表里没有消息 ${String(message)}`);
    }

    const frame = encodeFrame(definition, values, {
      version: this.version,
      sequence: this.sequence,
      systemId: this.systemId,
      componentId: this.componentId,
      ...(this.signer === undefined ? {} : { signer: this.signer }),
    });

    this.sequence = (this.sequence + 1) % 256;
    await this.transport.write(frame);
    this.framesOut += 1;
    this.bytesOut += frame.length;
    this.outRate.record(this.now(), frame.length);
  }

  /**
   * 申请某条消息的推送频率（Hz，0 表示停止推送）。
   *
   * 这是 MAVLink 自己的机制，所以放在会话层：链路该收到什么频率的遥测是链路的配置，
   * 不是某台机体特有的知识。返回 `false` 表示当前链路还没打开 —— 调用方应记录下来，
   * 在 `open()` 之后重放，而不是直接放弃。
   */
  async requestMessageInterval(messageId: number, rateHz: number): Promise<boolean> {
    if (this.transport.state !== 'open') return false;
    const applied = await this.sendCommandLong(511 /* MAV_CMD_SET_MESSAGE_INTERVAL */, [
      messageId,
      rateHz <= 0 ? -1 : Math.round(1_000_000 / rateHz),
    ]);
    return applied;
  }

  /**
   * 发一条 `COMMAND_LONG`。
   *
   * `param1..param7` 未给出的位填零 —— 这是 MAVLink 的约定（"不使用"的参数写 0），
   * 与"必需字段"的严格检查不同：一条命令用到哪几个参数由命令本身决定，
   * 会话层无从判断，硬性要求全部提供只会让每个调用点写一串零。
   */
  async sendCommandLong(
    command: number,
    params: readonly (number | undefined)[] = [],
    target: { systemId?: number; componentId?: number } = {},
  ): Promise<boolean> {
    if (this.transport.state !== 'open') return false;
    const values: Record<string, number> = {
      target_system: target.systemId ?? 0,
      target_component: target.componentId ?? 0,
      command,
      confirmation: 0,
    };
    for (let index = 0; index < 7; index += 1) {
      values[`param${index + 1}`] = params[index] ?? 0;
    }
    await this.send('COMMAND_LONG', values);
    return true;
  }

  /** 当前链路上的机体/组件（按最后一次活动时间排序，最活跃的在前） */
  vehicles(): readonly VehiclePresence[] {
    return [...this.presence.values()].sort((a, b) => b.lastSeenAt - a.lastSeenAt);
  }

  stats(): LinkStats {
    const now = this.now();
    return {
      framesIn: this.framesIn,
      framesOut: this.framesOut,
      bytesIn: this.bytesIn,
      bytesOut: this.bytesOut,
      framesPerSecond: this.inRate.rate(now).perSecond,
      bytesPerSecond: this.inRate.bytesPerSecond(now),
      crcErrors: this.parser.stats.crcErrors,
      unknownMessages: this.parser.stats.unknownMessages,
      droppedBytes: this.parser.stats.droppedBytes,
      malformedFrames: this.parser.stats.malformedFrames,
      truncatedPayloads: this.parser.stats.truncatedPayloads,
      signatureFailures: this.parser.stats.signatureFailures,
      lastFrameAt: this.lastFrameAt,
      silenceMs: this.lastFrameAt === null ? null : Math.max(0, now - this.lastFrameAt),
    };
  }

  /** 协议表未收录的消息 ID（固件比本地面站新时会出现） */
  unknownMessageIds(): readonly number[] {
    return [...this.parser.unknownMessageIds];
  }

  /**
   * 检查在线状态。定时器会调用它；测试也可以直接调用以摆脱对真实时钟的依赖。
   */
  tick(at?: number): void {
    const now = at ?? this.now();
    let changed = false;
    let anyOnline = false;

    for (const record of this.presence.values()) {
      const online = now - record.lastSeenAt <= this.heartbeatTimeoutMs;
      if (record.online !== online) {
        record.online = online;
        changed = true;
      }
      if (online) anyOnline = true;
    }

    if (changed) this.options.onPresenceChange?.(this.vehicles());

    if (this.currentState === 'connecting' || this.currentState === 'idle') return;
    if (this.currentState === 'error') return;

    if (anyOnline) {
      this.setState('online');
    } else if (this.presence.size > 0) {
      this.setState('offline', '超过判定阈值没有收到任何帧');
    } else {
      this.setState('silent');
    }
  }

  private handleFrame(frame: MavlinkFrame): void {
    const now = frame.receivedAt;
    this.framesIn += 1;
    this.lastFrameAt = now;
    this.inRate.record(now, frame.payload.length);

    const key = `${frame.systemId}:${frame.componentId}`;
    const record = this.presence.get(key);
    if (record === undefined) {
      this.presence.set(key, {
        key,
        systemId: frame.systemId,
        componentId: frame.componentId,
        firstSeenAt: now,
        lastSeenAt: now,
        frames: 1,
        online: true,
      });
      // 新出现的机体立刻算在线，不必等下一次 tick
      this.setState('online');
      this.options.onPresenceChange?.(this.vehicles());
    } else {
      record.lastSeenAt = now;
      record.frames += 1;
    }

    this.options.onFrame?.(frame);
  }

  private handleTransportState(state: TransportState, detail?: string): void {
    switch (state) {
      case 'open':
        // 状态在 open() 的后续逻辑里统一处理，这里只防止它把画面卡在 connecting
        if (this.currentState === 'connecting') this.setState('silent');
        return;
      case 'error':
        this.setState('error', detail ?? '链路异常');
        return;
      case 'closed':
        this.setState(this.currentState === 'error' ? 'error' : 'idle', detail);
        return;
      default:
        return;
    }
  }

  private setState(state: LinkState, detail?: string): void {
    if (this.currentState === state && this.stateDetail === detail) return;
    this.currentState = state;
    this.stateDetail = detail;
    this.options.onStateChange?.(state, detail);
  }
}

export { MavlinkSignatureVerifier, MavlinkSigner };
